import { passengerTypeAction } from "../../../Actions/Index";
import { getProcessBookingInitAction } from "../../../Actions/LogicActions/CommonActions/GetProcessBookingInitAction";
import { getPassengerDetailsAction } from "../../../Actions/LogicActions/Checkout/PassengerDetails/GetPassengerDetailsAction";
import { _eventBus } from "../../../core/events/EventBus";
import { StepDefinition } from "../../../core/flows/StepDefinition";
import { StepFlow } from "../../../core/flows/StepFlow";
import { checkoutTree } from "../../../Orchestrator/ConstantTree/CheckoutPage/CheckoutTree";
import { getRecoilStateValue, loadAtomFromLocalStorage, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import type { IPendingRefund } from "../../../Orchestrator/Recoils/AddonsAtoms";
import { CheckoutContext } from "../Context/CheckoutContext";

class CheckoutInitStep extends StepFlow {
    get stepName() { return "CheckoutInit_Step"; }

    async onEventSetup(): Promise<void> {
        this.registerEventSubscription(
            _eventBus.on({
                eventName: checkoutTree.Init,
                handler: async () => {
                    // Kick off /me in parallel — passenger-types and /me have
                    // no data dependency. PassengerDetailsFlow will await the
                    // same in-flight promise via deduplicate, so it joins
                    // instead of starting a second round-trip.
                    const isAnonymous = (getRecoilStateValue("isAnonymousStateAtom") as boolean) || false;
                    if (!isAnonymous) {
                        getPassengerDetailsAction.execute();
                    }

                    await passengerTypeAction.execute();
                    updateRecoilState("passengerTypesStateAtom", passengerTypeAction.result);
                    await getProcessBookingInitAction.execute();
                }
            })
        )
    }

    async onExecute(): Promise<void> {
        // Normalize addon state at every CheckoutFlow start so the CheckoutTripSummary
        // useEffect only emits GetBookingOffersAddOns after AddOnsFlow's subscriber is
        // registered (and any prior leg-keyed meal data is cleared).
        updateRecoilState("addonFlowStartedAtom", false);
        updateRecoilState("mealPreferencesResponseAtom", {});
        // Reset the Pay-button submitting flag — guards against a stuck
        // disabled button after a hard nav back to /checkout (e.g. gateway
        // cancel does window.location.href = /checkout) while a previous
        // submission was mid-flight.
        updateRecoilState("paymentSubmittingAtom", false);

        // pendingRefundFulfillmentIdsAtom is whitelisted (persisted to
        // localStorage) but never subscribed by a React component — only
        // actions read/write it. The useRecState hydration path therefore
        // never fires for it, so after a hard navigation (e.g. cancel-back
        // from the gateway: /checkout/cancel does window.location.href to
        // /checkout, full reload) the in-memory atom is at its default
        // (null) even though localStorage still holds the queued refund
        // ids. PrepareBookingSummary then mis-buckets the deselected
        // ancillaries as "active" (showing them as charges) instead of
        // pendingRefund ("To Refund"). Manually rehydrate from storage on
        // every CheckoutFlow start to close that gap.
        try {
            const storedPendingRefund =
                loadAtomFromLocalStorage<IPendingRefund>("pendingRefundFulfillmentIdsAtom");
            if (storedPendingRefund) {
                updateRecoilState("pendingRefundFulfillmentIdsAtom", storedPendingRefund);
            }
        } catch (e) {
            console.warn("[CheckoutInitStep] Failed to rehydrate pendingRefundFulfillmentIdsAtom:", e);
        }

        const isAnonymous = getRecoilStateValue<boolean>("isAnonymousStateAtom") || false;
        if (isAnonymous) {
            updateRecoilState("customerContactStateAtom", {
                firstName: "",
                lastName: "",
                email: "",
                phoneNumber: "",
            });
        }
        updateRecoilState("paymentSuccessModalAtom", false);

        if (typeof window !== "undefined" && localStorage.getItem("isUpgradeToPremiumFlow") === "true") {
            updateRecoilState("isUpgradeToPremiumStateAtom", true);
            updateRecoilState("isChangeDateStateAtom", true);
            updateRecoilState("isQuickExchangeStateAtom", true);
        }

        let context = this.getCtx<CheckoutContext>("checkoutContext")!;
        if (!context) {
            /**
             * This will set only if checkout init step trigger if checkout flow will start directly after login.
             * Initial set isAnonymous set true to avoid calling /me api without token
             * After that if will set false once token get successfully.
            */
            const checkoutContext = new CheckoutContext(true)
            this.setCtx<CheckoutContext>("checkoutContext", checkoutContext)
            context = this.getCtx<CheckoutContext>("checkoutContext")!;
        }

        ///DEMO:: TEMP ADD TICKET DETAILS FOR DEMO
        context.routeTo = ["PassengerDetailsFlow", "VoucherFlow", "SeatingMapFlow", "PaymentFlow", "AddOnsFlow"];     //"TicketDetailFlow"
        await _eventBus.emit({
            eventName: checkoutTree.Init
        });
        this.next();
    }

}
export const checkoutInitStepDefinition = new StepDefinition({
    stepName: "CheckoutInit_Step",
    stepClass: CheckoutInitStep
});